import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import { IEcommerceQuestionSchema } from '../../types.ts'
import style from './Header.module.scss'
import krogerLogo from '../assets/icons/logo.svg'
import searchIcon from '../assets/icons/search.svg'
import cartIcon from '../assets/icons/cart.svg'
import accountIcon from '../assets/icons/account.svg'

interface IHeaderProps {
  schema: IEcommerceQuestionSchema
  total: number
  findability: boolean
  clearSelection: () => void
  onCartOpen: () => void
  onHomeClick: () => void
}

const NAV_LINKS = ['navShop', 'navSave', 'navPickupDelivery', 'navServices', 'navPharmacyHealth']
const SUB_NAV_LINKS = [
  'subNavDigitalCoupons',
  'subNavWeeklyAd',
  'subNavFiveTimesCoupons',
  'subNavDailyDealDrops',
  'subNavSummer',
  'subNavMatchDay',
  'subNavMealPlanning',
  'subNavStoreLocator'
]

const Header = ({ schema, total, findability, clearSelection, onCartOpen, onHomeClick }: IHeaderProps) => {
  const viewport = useViewport()
  const { t } = useSurveyLocale()

  if (viewport.mobile) {
    return (
      <header className={style.headerMobile} data-testid="SW-EC-Header">
        <button
          type="button"
          className={style.backButton}
          onClick={onHomeClick}
          aria-label={t('questions', 'ec', 'kroger', 'back')}
        >
          <i className="pi pi-chevron-left" />
        </button>
        <div className={style.searchMobile} onClick={clearSelection}>
          <img src={searchIcon} alt="" role="presentation" className={style.searchMobileIcon} />
          <span
            className={style.searchMobileText}
            dangerouslySetInnerHTML={{
              __html: schema.search_condition || t('questions', 'ec', 'kroger', 'searchMobile')
            }}
          />
        </div>
        <EmptyLink color="#0e51a1">
          <span className={style.scanButton} role="img" aria-label={t('questions', 'ec', 'kroger', 'scan')}>
            <svg
              viewBox="0 0 512 512"
              width="22"
              height="22"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={38}
            >
              <path d="M342 444h46a56 56 0 0 0 56-56v-46m0-172v-46a56 56 0 0 0-56-56h-46M170 444h-46a56 56 0 0 1-56-56v-46m0-172v-46a56 56 0 0 1 56-56h46" />
            </svg>
          </span>
        </EmptyLink>
      </header>
    )
  }

  const cartInner = (
    <>
      <img src={cartIcon} alt="" role="presentation" className={style.cartIcon} />
      {total > 0 && <span className={style.cartBadge}>{total > 99 ? '99+' : total}</span>}
    </>
  )
  const cart = findability ? (
    <EmptyLink color="#0e51a1">
      <div className={style.cart}>{cartInner}</div>
    </EmptyLink>
  ) : (
    <button type="button" className={style.cart} onClick={onCartOpen}>
      {cartInner}
    </button>
  )

  const searchBox = (
    <div className={style.search} onClick={clearSelection}>
      <div
        className={style.searchText}
        dangerouslySetInnerHTML={{ __html: schema.search_condition || t('questions', 'ec', 'kroger', 'search') }}
      />
      <img src={searchIcon} alt="" role="presentation" className={style.searchIcon} />
    </div>
  )

  return (
    <header className={style.header} data-testid="SW-EC-Header">
      <div className={style.topBar}>
        <img
          src={krogerLogo}
          alt={t('questions', 'ec', 'kroger', 'store')}
          className={style.logo}
          onClick={onHomeClick}
        />
        <nav className={style.nav}>
          {NAV_LINKS.map(key => (
            <EmptyLink key={key} color="#0e51a1">
              <span className={style.navItem}>{t('questions', 'ec', 'kroger', key)}</span>
            </EmptyLink>
          ))}
        </nav>
        {searchBox}
        <EmptyLink color="#0e51a1">
          <span className={style.account}>
            <img src={accountIcon} alt="" role="presentation" className={style.accountIcon} />
            {t('questions', 'ec', 'kroger', 'signIn')}
            <i className={`pi pi-chevron-down ${style.chevron}`} />
          </span>
        </EmptyLink>
        {cart}
      </div>
      <div className={style.subNav}>
        <div className={style.location}>
          <EmptyLink color="#0e51a1">
            <span className={style.locationPickup}>
              {t('questions', 'ec', 'kroger', 'pickupAtStore')}
              <i className={`pi pi-chevron-down ${style.chevron}`} />
            </span>
          </EmptyLink>
          <span className={style.locationDivider}>|</span>
          <span className={style.locationAddress}>{t('questions', 'ec', 'kroger', 'storeAddress')}</span>
        </div>
        <div className={style.subNavLinks}>
          {SUB_NAV_LINKS.map(key => (
            <EmptyLink key={key} color="#0e51a1">
              <span className={style.subNavItem}>{t('questions', 'ec', 'kroger', key)}</span>
            </EmptyLink>
          ))}
        </div>
      </div>
    </header>
  )
}

export default Header
